import React, { useState, useEffect } from "react";
import config from "lib/fr-config";
import TextButtonView from "presentational/common/TextButtonView";
import { withStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import { renderOutfitImage } from "lib/renderImageForDownload45BrandListType";
import { getDateText } from "lib/dateFormatString";
import { translationIndex } from "lib/translationIndex";
import { getLangType } from "lib/commonUtil";
import ItemCategory from "lib/itemCategory";
import TXT from "presentational/common/multinational/TXT";
import Tooltip from "@material-ui/core/Tooltip";
import { withStore } from "store/storeHandler";
import PropTypes from "prop-types";

// const colorCss = config.css.color;

const BOTTOM_OUTER_PADDING = 20;
const SIDE_OUTER_PADDING = 34;
const PADDING_BETWEEN_CONTENT = 24;

/** 임시 하드코딩. 나중에는 사용자의 국가로 설정하자. */
const USERS_LOCATION = "US";

const MAX_LENGTH_OF_BRAND_LIST = 10;
const MAX_LENGTH_OF_BRAND_STRING = 22;

const CustomDialog = withStyles({
  paper: {
    maxWidth: 600,
    margin: 0
  },
  container: {
    backgroundColor: "rgba(240, 240, 240, 1)"
  }
})(Dialog);
const CustomDialogContent = withStyles({
  root: {
    padding: `0`,
    marginBottom: PADDING_BETWEEN_CONTENT
  }
})(DialogContent);
const CustomDialogActions = withStyles({
  root: {
    margin: `${0}px ${SIDE_OUTER_PADDING}px ${BOTTOM_OUTER_PADDING}px`,
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flexWrap: "wrap"
  }
})(DialogActions);
const CustomTooltip = withStyles({
  tooltip: {
    // borderRadius: 0
    // backgroundColor: colorCss.colorOfTooltipPrimary
  },
  // arrow: {
  //   // color: colorCss.colorOfTooltipPrimary
  // },
  popper: { zIndex: 2000 }
})(Tooltip);

const css = {
  paper: {
    backgroundColor: "rgba(255, 255, 255, 0)"
  },
  imageWrapper: { position: "relative" },
  image: { width: "100%", height: "auto" }
};

DownloadImgModalView.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDownloadComplete: PropTypes.func,
  downloadImgInfo: PropTypes.shape({
    /** 필수 */
    downloadImgType: PropTypes.oneOf(["brand"]),
    imageSrc: PropTypes.string,
    date: PropTypes.string,
    username: PropTypes.string,

    /** 브랜드리스트 */
    itemData: PropTypes.array
  })
};

DownloadImgModalView.defaultProps = {
  onDownloadComplete: () => {}
};

function DownloadImgModalView(props) {
  const {
    open,
    onClose,
    onDownloadComplete,
    downloadImgInfo,
    AppErrorActions,
    NoticeModalActions
  } = props;
  const [hasImageRendered, setHasImageRendered] = useState(false);
  const [imgSrc, setImgSrc] = useState(null);
  const [filename, setFilename] = useState(null);

  useEffect(() => {
    setImageSrc();
  });

  const getTruncatedBrandName = brandName => {
    if (typeof brandName !== "string") {
      return "";
    }
    if (brandName.length > MAX_LENGTH_OF_BRAND_STRING) {
      return brandName.slice(0, MAX_LENGTH_OF_BRAND_STRING) + "...";
    }
    return brandName;
  };

  const checkWrongCategory = itemData => {
    const returnArr = [];

    for (let i = 0; i < itemData.length; i++) {
      const { category } = itemData[i];

      try {
        ItemCategory.getCategoryOrder(category);
        returnArr.push(itemData[i]);
      } catch (error) {
        AppErrorActions.setSafeAppError(error);
        continue;
      }
    }

    return returnArr;
  };

  const getItemBrandList = () => {
    const { itemData } = downloadImgInfo;

    const itemBrandList = [];
    const langType = getLangType(USERS_LOCATION);

    if (!itemData) return itemBrandList;

    const validItemData = checkWrongCategory(itemData);

    validItemData.sort(
      (a, b) =>
        ItemCategory.getCategoryOrder(a.category) -
        ItemCategory.getCategoryOrder(b.category)
    );

    for (let i = 0; i < validItemData.length; i++) {
      if (i >= MAX_LENGTH_OF_BRAND_LIST) break;

      const element = validItemData[i];
      const { brand, category } = element;

      if (!brand || !category) {
        AppErrorActions.setSafeAppError(new Error(`wrong itemData`));
        return itemBrandList;
      }

      const categoryName =
        translationIndex.categoryList.data[category][langType].itemSingleName ||
        "";

      if (categoryName.length === 0) {
        AppErrorActions.setSafeAppError(
          new Error(`null itemSingleName: ${category}`)
        );
      }

      itemBrandList.push({
        brand: getTruncatedBrandName(brand),
        category: categoryName
      });
    }
    return itemBrandList;
  };

  const setImageSrc = async () => {
    if (!(open && !hasImageRendered)) return null;

    const { imageSrc, date, username } = downloadImgInfo;

    const dateCaptionStr = getDateText({
      inputDateISOString: date,
      outputType: "outputYM",
      location: USERS_LOCATION
    });

    const itemBrandList = getItemBrandList();

    try {
      const { src, filename } = await renderOutfitImage({
        imageSrc,
        dateCaptionStr,
        usernameStr: username,
        itemBrandList
      });

      setImgSrc(src);
      setFilename(filename);
      setHasImageRendered(true);
    } catch (error) {
      AppErrorActions.setSafeAppError(error);
      NoticeModalActions.openModal({
        content: <TXT name="msg_commonNetworkError" />
      });
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
    setHasImageRendered(false);
  };

  const handleDownloadClick = () => {
    const link = document.createElement("a");
    link.download = filename;
    link.href = imgSrc;
    link.click();

    handleClose();
    onDownloadComplete();
  };

  return (
    <CustomDialog
      open={open && hasImageRendered}
      onClose={handleClose}
      scroll="paper"
      PaperProps={{ square: true, elevation: 0, style: css.paper }}
      transitionDuration={config.css.animation.modalDurationMilliSec}
    >
      <CustomDialogContent>
        <CustomTooltip
          open={true}
          title={<TXT name="msg_tooltipIntroduceDownloadImage" />}
          placement={"top"}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          TransitionProps={{ timeout: 800 }}
          arrow
        >
          <div style={css.imageWrapper}>
            <img alt="" src={imgSrc} style={css.image} />
          </div>
        </CustomTooltip>
      </CustomDialogContent>
      <CustomDialogActions>
        <TextButtonView
          onClick={handleDownloadClick}
          paddingSize="large"
          design="default"
        >
          <TXT name="btnModal_downloadImage" />
        </TextButtonView>
        <TextButtonView
          onClick={handleClose}
          paddingSize="large"
          design="default"
          disableRightMargin
        >
          <TXT name="btnModal_close" />
        </TextButtonView>
      </CustomDialogActions>
    </CustomDialog>
  );
}

export default withStore({
  dispatchMap: ["appError", "noticeModal"]
})(DownloadImgModalView);
